import { useEffect, useState } from "react";
import { CategoryType } from "../components/CategorySelect";

import { SearchResult, search } from "../lib/robozinho/search";

const PAGES_TO_PRELOAD = 2;

export function useSearch(
    defaultQuery: string = "",
    defaultCategory: CategoryType = "hetero"
): [
    SearchResult | null,
    (newQuery: string, category: CategoryType) => void,
    boolean,
    () => void
] {
    const [result, setResult] = useState<SearchResult | null>(null);
    const [query, setQuery] = useState(defaultQuery);
    const [category, setCategory] = useState(defaultCategory);
    const [page, setPage] = useState(0);
    const [isLoading, setLoading] = useState(false);
    const [cancelPreload, setCancelPreload] = useState(false);

    const [isPreloading, setPreloading] = useState(false);
    const [preloadedData, setPreloadedData] = useState<SearchResult[]>([]);

    async function preload() {
        if (isPreloading) {
            throw new Error("Already preloading");
        }

        setPreloading(true);

        const preloadedDataCopy = [...preloadedData];

        for (let i = 0; i < PAGES_TO_PRELOAD; i++) {
            if (cancelPreload) break;

            if (!preloadedDataCopy[i]) {
                if (!result || page + i < result.pagination.numPages) {
                    preloadedDataCopy[i] = await search(
                        query,
                        category,
                        page + 1 + i
                    );

                    if (!cancelPreload) {
                        setPreloadedData(preloadedDataCopy);
                    }
                }
            }
        }

        setPreloading(false);
        setCancelPreload(false);
    }

    useEffect(() => {
        if (query) {
            setLoading(true);
            search(query, category, 0).then((searchResult) => {
                setResult(searchResult);
                setLoading(false);
            });
        }
    }, []);

    useEffect(() => {
        if (query && !isPreloading && preloadedData.length < PAGES_TO_PRELOAD) {
            preload();
        }
    }, [isPreloading, preloadedData, cancelPreload]);

    useEffect(() => {
        if (!isPreloading && isLoading && preloadedData[0]) {
            const newResult = preloadedData[0];
            setResult({
                pagination: newResult.pagination,
                channels: [
                    ...(result ? result.channels : []),
                    ...newResult.channels,
                ],
            });

            setLoading(false);
            setPreloadedData(preloadedData.slice(1));
            setPage(newResult.pagination.currentPage);
        }
    }, [isLoading, isPreloading, preloadedData]);

    const newSearch = (newQuery: string, newCategory: CategoryType) => {
        if (newQuery) {
            setLoading(true);
            setResult(null);
            setCancelPreload(true);
            setPreloadedData([]);

            search(newQuery, newCategory, 0).then((searchResult) => {
                setResult(searchResult);
                setQuery(newQuery);
                setCategory(newCategory);

                // Reset all states
                setLoading(false);
                setPage(0);
            });
        }
    };

    const loadMore = () => {
        if (!result || page + 1 < result.pagination.numPages) {
            setLoading(true);
        }
    };

    return [result, newSearch, isLoading, loadMore];
}
